class DeliveryEstimation extends HTMLElement {
  constructor() {
    super();

    this.minDateElement = null;
    this.maxDateElement = null;
    this.deliveryTextElement = null;
    this.betweenTextElement = null;

    this.config = {
      locale: 'fr',
      minDays: 14,
      maxDays: 23,
      noSaturday: false,
      noSunday: false,
      dateFormat: 'in_letters',
      showWeekday: true,
      cutoffHour: null,
      fallbackText: 'Livraison en 5 à 12 jours',
      estimationText: 'Livraison estimée entre le',
      betweenText: 'et le',
    };
  }

  connectedCallback() {
    this.refresh();
  }

  refresh() {
    this.minDateElement = this.querySelector('[data-ref="min-date"]');
    this.maxDateElement = this.querySelector('[data-ref="max-date"]');
    this.deliveryTextElement = this.querySelector('[data-ref="delivery-text"]');
    this.betweenTextElement = this.querySelector('[data-ref="between-text"]');

    this.#loadConfiguration();
    this.#calculateAndDisplayDates();
  }

  #loadConfiguration() {
    this.config.locale = this.dataset.locale || 'fr';
    this.config.minDays = parseInt(this.dataset.minDays, 10);
    this.config.maxDays = parseInt(this.dataset.maxDays, 10);
    this.config.noSaturday = this.dataset.noSaturday === 'true';
    this.config.noSunday = this.dataset.noSunday === 'true';
    this.config.dateFormat = this.dataset.dateFormat || 'in_letters';
    this.config.showWeekday = this.dataset.showWeekday === 'true';
    this.config.fallbackText = this.dataset.fallbackText || 'Livraison en 5 à 12 jours';
    this.config.estimationText = this.dataset.estimationText || 'Livraison estimée entre le';
    this.config.betweenText = this.dataset.betweenText || 'et le';

    if (!Number.isFinite(this.config.minDays) || this.config.minDays < 1) {
      this.config.minDays = 14;
    }
    if (!Number.isFinite(this.config.maxDays) || this.config.maxDays < this.config.minDays) {
      this.config.maxDays = this.config.minDays + 9;
    }

    const cutoffHour = this.dataset.cutoffHour;
    if (cutoffHour && cutoffHour !== '') {
      const parsedCutoff = parseInt(cutoffHour, 10);
      this.config.cutoffHour = Number.isFinite(parsedCutoff) ? parsedCutoff : null;
    } else {
      this.config.cutoffHour = null;
    }
  }

  #calculateAndDisplayDates() {
    try {
      const minDate = this.#calculateDeliveryDate(this.config.minDays);
      const maxDate = this.#calculateDeliveryDate(this.config.maxDays);
      const minLabel = this.#formatDate(minDate);
      const maxLabel = this.#formatDate(maxDate);

      if (!minLabel || !maxLabel) {
        this.#showFallback();
        return;
      }

      this.#showDates(minLabel, maxLabel);
    } catch (error) {
      if (!this.#hasRenderedDates()) {
        this.#showFallback();
      }
    }
  }

  #hasRenderedDates() {
    const minText = this.minDateElement?.textContent?.trim();
    const maxText = this.maxDateElement?.textContent?.trim();
    return Boolean(minText && maxText);
  }

  #showDates(minLabel, maxLabel) {
    if (!minLabel || !maxLabel) {
      this.#showFallback();
      return;
    }

    if (this.deliveryTextElement) {
      this.deliveryTextElement.textContent = this.config.estimationText;
    }

    if (this.minDateElement) {
      this.minDateElement.hidden = false;
      this.minDateElement.textContent = minLabel;
    }

    if (this.maxDateElement) {
      this.maxDateElement.hidden = false;
      this.maxDateElement.textContent = maxLabel;
    }

    if (this.betweenTextElement) {
      this.betweenTextElement.hidden = false;
      this.betweenTextElement.textContent = this.config.betweenText;
    }
  }

  #showFallback() {
    if (this.deliveryTextElement) {
      this.deliveryTextElement.textContent = this.config.fallbackText;
    }

    if (this.minDateElement) {
      this.minDateElement.textContent = '';
      this.minDateElement.hidden = true;
    }

    if (this.betweenTextElement) {
      this.betweenTextElement.textContent = this.config.betweenText;
      this.betweenTextElement.hidden = true;
    }

    if (this.maxDateElement) {
      this.maxDateElement.textContent = '';
      this.maxDateElement.hidden = true;
    }
  }

  #calculateDeliveryDate(daysToAdd) {
    const now = new Date();
    const startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    if (this.config.cutoffHour !== null && now.getHours() >= this.config.cutoffHour) {
      startDate.setDate(startDate.getDate() + 1);
    }

    let businessDaysAdded = 0;
    const currentDate = new Date(startDate);

    while (businessDaysAdded < daysToAdd) {
      currentDate.setDate(currentDate.getDate() + 1);

      const dayOfWeek = currentDate.getDay();
      const isSaturday = dayOfWeek === 6;
      const isSunday = dayOfWeek === 0;
      const isBusinessDay = !((isSaturday && this.config.noSaturday) || (isSunday && this.config.noSunday));

      if (isBusinessDay) {
        businessDaysAdded++;
      }
    }

    while (this.#isNonDeliveryDay(currentDate)) {
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return currentDate;
  }

  #isNonDeliveryDay(date) {
    const dayOfWeek = date.getDay();
    return (dayOfWeek === 6 && this.config.noSaturday) || (dayOfWeek === 0 && this.config.noSunday);
  }

  #formatDate(date) {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
      return '';
    }

    const options = {};

    if (this.config.dateFormat === 'numbers') {
      options.day = 'numeric';
      options.month = 'numeric';
    } else {
      options.day = 'numeric';
      options.month = 'long';
    }

    if (this.config.showWeekday) {
      options.weekday = 'long';
    }

    const locale = this.config.locale || 'fr';
    const formatter = new Intl.DateTimeFormat(locale, options);
    return this.#capitalizeFirstLetter(formatter.format(date));
  }

  #capitalizeFirstLetter(str) {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  }
}

if (!customElements.get('delivery-estimation')) {
  customElements.define('delivery-estimation', DeliveryEstimation);
}
